import { InactivityStorageKeys } from '~/hooks/useInactivityTracker/InactivityTracker.constants';

/**
 * Persist the shared, cross-tab "last activity" timestamp (ms since epoch).
 * This is the single source of truth used to decide whole-session inactivity,
 * so a single idle tab cannot declare the session inactive while another tab is
 * still being used.
 */
export const writeLastActivity = (): void => {
  try {
    localStorage.setItem(
      InactivityStorageKeys.userLastActivity,
      String(Date.now())
    );
  } catch {
    // Storage can be unavailable (Safari private mode, quota exceeded, blocked
    // by policy). Treat the write as a no-op so inactivity tracking degrades
    // gracefully instead of throwing and breaking the app.
  }
};

/**
 * Read the shared, cross-tab "last activity" timestamp.
 * @returns the timestamp in ms, or null when it is missing/invalid so callers
 * can apply their own fallback (fail-secure vs. benign) as appropriate.
 */
export const readLastActivity = (): number | null => {
  try {
    const stored = Number(
      localStorage.getItem(InactivityStorageKeys.userLastActivity)
    );
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  } catch {
    // If storage is unavailable, return null so callers apply their existing
    // fallback (fail-secure in getLastActivity, benign in getInitialCountdown)
    // rather than crashing.
    return null;
  }
};

/**
 * Compute the initial countdown (ms) the inactivity modal should display, based
 * on the shared last-activity timestamp so every tab's countdown stays in sync
 * and a tab that is already past the full session timeout logs out immediately.
 * @param idleTimeout - idle duration (ms) before the modal appears
 * @param modalTimeout - full modal countdown duration (ms)
 * @param lastActivity - shared last-activity timestamp (ms), or null if unknown
 * @param now - current time (ms); injectable for testing
 * @returns remaining countdown clamped to [0, modalTimeout]
 */
export const getInitialCountdown = (
  idleTimeout: number,
  modalTimeout: number,
  lastActivity: number | null,
  now: number = Date.now()
): number => {
  // Without a known idle window we cannot derive elapsed time reliably, so fall
  // back to a full countdown rather than risk an immediate/incorrect logout.
  if (!idleTimeout) {
    return modalTimeout;
  }
  const last = lastActivity ?? now;
  const remaining = idleTimeout + modalTimeout - (now - last);
  const clamped = Math.max(0, Math.min(modalTimeout, remaining));
  // Floor to a whole second so the 1s countdown interval lands exactly on 0
  // (rather than a fractional value that could tick negative) and the displayed
  // countdown shows clean seconds.
  return Math.floor(clamped / 1000) * 1000;
};
